/**
 * Design upload/select + deterministic slug dedup (005 T006).
 * Turns picked files into DesignInputs with unique, stable slugs.
 */
import type { DesignInput, PickedFile } from "@genz/platform";
import { categoryFromPath, dedupeSlugs, slugify, stripExt } from "../export/refs.js";

let counter = 0;
function nextId(): string {
  counter += 1;
  return `design-${Date.now().toString(36)}-${counter}`;
}

export function toDesignInputs(files: PickedFile[]): DesignInput[] {
  const baseSlugs = files.map((f) => slugify(f.name));
  const unique = dedupeSlugs(baseSlugs);
  return files.map((f, i) => ({
    designId: nextId(),
    filename: f.name,
    designSlug: unique[i],
    bytes: f.bytes,
    bytesRef: f.path,
    // Preserve the source subfolder as a category (spec 014).
    category: categoryFromPath(f.path),
    // Human template code from the FILE name only (`f.name` is always the basename,
    // never a folder-qualified path — see PickedFile in @genz/platform), extension
    // stripped. No dedup: the design code is display/search sugar, unlike designSlug.
    // `|| undefined`: a filename that's entirely an extension (e.g. ".png") strips to
    // "", and every consumer falls back on `code ?? name` — an empty string isn't
    // nullish, so that OR-branch never fires and rows render blank. Normalize the
    // empty case to undefined here, matching the trim-or-undefined pattern used
    // elsewhere in this feature.
    code: stripExt(f.name) || undefined,
  }));
}
